import { BeneficiaryType } from '@seapoint/server/enums';
import { ChevronRightIcon } from 'lucide-react';
import { Avatar } from '../../../../design-system/avatar/avatar/avatar';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { SupportedCountryCurrency } from '../../../../types/supported-country-currency';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { getCurrencyFlag } from '../../../../utils/get-currency-flag';

type AccountDetailsConfig =
  | {
      beneficiaryType: BeneficiaryType.IBAN;
      iban: string;
    }
  | {
      beneficiaryType: BeneficiaryType.SCAN;
      accountNumber: string;
      sortCode: string;
    };

type BeneficiaryCardProps = {
  name: string;
  // There is no country info in the beneficiary object today, so using the currency for now
  // https://www.figma.com/design/3b1S7xbXjjWIobGEmEfMdA?node-id=3523-13561&m=dev#1173577959
  // TODO: replace with beneficiary country once we have it
  currency: SupportedCountryCurrency;
  accountDetails: AccountDetailsConfig;
  onSelectBeneficiary: () => void;
};

export const BeneficiaryCard = ({ name, currency, accountDetails, onSelectBeneficiary }: BeneficiaryCardProps) => {
  const currencyFlag = getCurrencyFlag(currency);
  const accountDetailsText = getAccountDetailsText({ accountDetails });

  return (
    <Button
      variant="ghost"
      size="none"
      onClick={onSelectBeneficiary}
      aria-label={`Make a payment to ${name} with ${currency} currency`}
      className="w-full rounded-lg"
    >
      <Inline
        space={3}
        className="w-full rounded-lg border border-seapoint-gray-border bg-white p-3 hover:bg-seapoint-gray-background-hover"
      >
        <Avatar name={name} size="sm" />

        <Stack space={1} className="min-w-0 flex-1">
          <h3 className="w-full truncate text-nowrap text-start text-sm font-medium text-black">{name}</h3>

          <p className="w-full truncate text-start text-xs font-normal text-seapoint-gray-text">{accountDetailsText}</p>
        </Stack>

        <Inline wrap="nowrap" space={2}>
          <div className="h-6 w-6 overflow-clip rounded-full">
            <img src={currencyFlag} alt="" loading="eager" fetchPriority="high" className="h-full w-full" />
          </div>

          <ChevronRightIcon size={20} color="#86868B" />
        </Inline>
      </Inline>
    </Button>
  );
};

type GetAccountDetailsTextArgs = {
  accountDetails: AccountDetailsConfig;
};

const getAccountDetailsText = ({ accountDetails }: GetAccountDetailsTextArgs) => {
  const { beneficiaryType } = accountDetails;

  switch (beneficiaryType) {
    case BeneficiaryType.IBAN:
      return accountDetails.iban;
    case BeneficiaryType.SCAN:
      return `${accountDetails.accountNumber} • ${accountDetails.sortCode}`;
    default:
      return exhaustiveCheck(beneficiaryType);
  }
};
